import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { sanitize } from '../lib/sanitize';
import SeoHead from '../components/seo/SeoHead';
import styles from '../components/home/FeaturedCollections.module.css';

interface Category {
  id: string;
  name: string;
  description: string | null;
  short_description: string | null;
  image_url: string | null;
  slug: string | null;
}

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, description, short_description, image_url, slug')
        .eq('is_visible', true)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) setError(error.message);
      else setCategories(data || []);
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className={styles.loading}>Učitavanje kategorija...</div>;
  if (error) return <div className={styles.error}>Greška: {error}</div>;

  const itemListLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: categories.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      url:
        typeof window !== 'undefined'
          ? new URL(`/category/${c.slug || c.id}`, window.location.origin).toString()
          : undefined,
    })),
  };

  return (
    <div style={{ padding: '2rem 1rem', maxWidth: 1200, margin: '0 auto' }}>
      <SeoHead
        title="Sve kategorije"
        description="Pregledaj sve kategorije proizvoda u našoj online prodavnici."
        jsonLd={itemListLd}
      />
      <h1 style={{ marginBottom: '1.5rem' }}>Sve kategorije</h1>
      <div className={styles.collectionsGrid}>
        {categories.map(category => (
          <Link
            to={`/category/${category.slug || category.id}`}
            key={category.id}
            className={styles.collectionCard}
          >
            <div className={styles.imageContainer}>
              <img
                src={category.image_url || ''}
                srcSet={
                  category.image_url && /\.jpe?g$/i.test(category.image_url)
                    ? `${category.image_url.replace(/\.(jpe?g)$/i, '-400.$1')} 400w, ${category.image_url} 1000w`
                    : undefined
                }
                sizes="(max-width: 600px) 50vw, (max-width: 1024px) 33vw, 300px"
                alt={category.name}
                width={400}
                height={400}
                loading="lazy"
                decoding="async"
                onError={e => {
                  (e.target as HTMLImageElement).src =
                    'https://via.placeholder.com/300x200?text=Kategorija';
                }}
              />
            </div>
            <div className={styles.collectionInfo}>
              <h3>{category.name}</h3>
              {category.short_description && (
                <p dangerouslySetInnerHTML={{ __html: sanitize(category.short_description) }} />
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default CategoriesPage;
